import React, { useCallback, useMemo } from 'react';
import { DefaultNoRowsOverlay } from '../../ConfigurableTable/Components/NoRowsOverlay';
import { SimData } from '../constants/sim';
import {
  DataGridPro,
  DataGridProProps,
  GridColDef,
  GridColumnOrderChangeParams,
  GridColumnVisibilityModel,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowId,
  GridRowSelectionModel,
  GridSeparatorIcon,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import Box from '@mui/material/Box';
import { getRowClassName, getRowId } from './utils/simListKeysUtils';
import {
  ColumnConfiguration,
  DEFAULT_COLUMN_MAX_WIDTH,
  DEFAULT_WIDTH,
  TABLE_VARIANT,
  TableVariant,
} from './constants/columnConfigurationsConstants';
import { useDataGridDialogs } from '../../ConfigurableTable/hooks/useDataGridDialogs';
import { Action } from '../../ConfigurableTable/models/configurableTable';
import { useSimColumnsTranslations } from './hooks/useSimColumnTranslations';
import { HeaderCell } from 'src/simInventory/Components/ConfigurableTable/tableCells/HeaderCell';
import { actionPermissionFilter } from '../../ConfigurableTable/utils/actionsFiltering';
import { useAbility } from 'src/permissions/hooks/useAbility';
import { SimInventoryCan } from 'src/permissions/PermissionProvider';
import { Actions, Subjects } from 'src/permissions/ability';
import { TableSettings } from '../../ConfigurableTable/Settings/TableSettings';
import { ActionCell } from 'common/components/ConfigurableTable/Components/Cells/ActionCell';
import { Sorting } from '../../Filters/constants/sorting';
import { useTranslation } from 'react-i18next';
import { TextCell } from '../../ConfigurableTable/Components/Cells/TextCell';
import { FIELD_TYPE } from 'src/admin/customFields/entities/customFields.entity';
import { Pagination } from '../../ConfigurableTable/Components/Pagination/Pagination';
import { useIsSessionDetailsListDetailsLoading } from 'src/simInventory/hooks/useSessionListDetails';
import { useSimColumnsConfiguration } from './hooks/useSimColumnConfiguration';
import { useTheme } from '@mui/material/styles';
import { useRootFontScale } from '../../ConfigurableTable/Components/Base/useRootFontScale';

type SimTableProps = {
  ['data-testid']?: string;
  actions?: Action[];
  configuration: ColumnConfiguration[];
  enabledVariant: TableVariant;
  sort: { columnName: string; sort: Sorting } | null;
  onSortModelChange: (model: GridSortModel) => void;
  updateWidthOfColumn: (columnName: string, width: 'auto' | number, isCustomField: boolean) => void;
  reorder: (srcIndex: number, dstIndex: number) => void;
  updateVisibilityOfColumns: (visiblityModel: Record<string, boolean>) => void;
  updateVariant: (variant: TableVariant) => void;
  revertToDefault: () => void;
  rows: SimData[];
  page: number;
  pageSize: number;
  rowCount: number;
  onPaginationChange: (pagination: GridPaginationModel) => void;
  onRowSelectionModelChange?: DataGridProProps['onRowSelectionModelChange'];
  selectedIds?: Set<GridRowId>;
};

const DefaultGridSeparatorIcon = () => <GridSeparatorIcon data-testid="SeparatorIcon" />;

export const SimTable = React.memo(
  ({
    onPaginationChange,
    actions,
    enabledVariant,
    configuration,
    sort,
    updateWidthOfColumn,
    reorder,
    updateVisibilityOfColumns,
    updateVariant,
    revertToDefault,
    rows,
    page,
    pageSize,
    'data-testid': testid,
    selectedIds,
    ...props
  }: SimTableProps) => {
    const ability = useAbility();
    const { idLabelMapForFlatTable, idLabelMapForCombinedTable } = useSimColumnsTranslations();

    const { dialogs, selectDialog, unselectDialog, dialogActions } = useDataGridDialogs({
      actions,
    });

    const isSessionDetailsLoading = useIsSessionDetailsListDetailsLoading();

    const SIM_COLUMNS_DEF = useSimColumnsConfiguration();

    const fontScale = useRootFontScale();

    const { t } = useTranslation();
    const columns = useMemo(() => {
      const columns = configuration?.reduce(
        (enabledColumns: GridColDef[], column: ColumnConfiguration) => {
          const primaryColumnDefaults = SIM_COLUMNS_DEF[column.name];
          const defaultPrimaryColumnWidth = primaryColumnDefaults?.width ?? DEFAULT_WIDTH;
          const defaultPrimaryColumnMaxWidth =
            primaryColumnDefaults?.maxWidth ?? DEFAULT_COLUMN_MAX_WIDTH;

          const secondaryColumnDefaults = SIM_COLUMNS_DEF[column.children[0]?.name];

          const columnDefinition: GridColDef = {
            field: column.name,
            minWidth: 70 * fontScale,
            maxWidth: defaultPrimaryColumnMaxWidth * fontScale,
            width:
              column.width === 'auto'
                ? defaultPrimaryColumnWidth * fontScale
                : +column.width * fontScale,

            renderHeader: () => (
              <HeaderCell
                column={column}
                primaryText={primaryColumnDefaults?.getHeaderTitle({ t }) ?? column.name}
                secondaryText={
                  secondaryColumnDefaults?.getHeaderTitle({ t, isChildren: true }) ??
                  column.children[0]?.name
                }
              />
            ),
            renderCell: (params: GridRenderCellParams) => {
              if (primaryColumnDefaults?.cellRender) {
                return primaryColumnDefaults.cellRender({ column, row: params.row, ability });
              }

              if (primaryColumnDefaults?.getCellText) {
                const value = primaryColumnDefaults?.getCellText({
                  value: params.row[column.name],
                });

                if (column.children[0]?.name) {
                  const subValue = secondaryColumnDefaults?.getCellText
                    ? secondaryColumnDefaults?.getCellText({
                        value: params.row[column.children[0]?.name],
                      })
                    : params.row[column.children[0]?.name];

                  return <TextCell value={value} subValue={subValue ?? ''} />;
                }

                return <TextCell value={value} />;
              }

              if (column.isCustomField && params.row[column.name.toLowerCase()]) {
                if (params.row[column.name.toLowerCase()].dataType === FIELD_TYPE.Dropdown) {
                  const text = params.row[column.name.toLowerCase()].selectionOption;
                  return <TextCell value={text} />;
                }
                const text = params.row[column.name.toLowerCase()].textValue;
                return <TextCell value={text} />;
              }

              const isCustomFieldColumn = !(column.name in SIM_COLUMNS_DEF);
              if (isCustomFieldColumn) {
                return <TextCell value={params.row[column.name]} />;
              }

              return (
                <TextCell
                  value={params.row[column.name]?.toString()}
                  subValue={
                    column.children.length
                      ? params.row[column.children[0]?.name] || ''
                      : params.row[column.children[0]?.name]
                  }
                />
              );
            },
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
          };
          enabledColumns.push(columnDefinition);

          return enabledColumns;
        },
        [],
      );

      if (actions) {
        const mappedAction = actions.filter(actionPermissionFilter(ability)).map((action) => {
          if (action.type === 'dialog') {
            return {
              ...action,
              actionCallback: (data: any) => selectDialog(action.id, data),
            };
          }

          return action;
        });

        columns.push({
          field: 'actions',
          width: 60 * fontScale,
          minWidth: 60 * fontScale,
          maxWidth: 60 * fontScale,
          resizable: false,
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          disableReorder: true,
          pinnable: false,
          cellClassName: 'sticky',
          headerClassName: 'header-sticky',
          renderHeader: () => (
            <SimInventoryCan I={Actions.edit} a={Subjects.simProfilesTableConfiguration}>
              <TableSettings
                columnConfig={configuration}
                handleWidthChange={updateWidthOfColumn}
                handleVariantChange={handleVariantChange}
                handleRevert={revertToDefault}
                handleDrop={reorder}
                variant={enabledVariant}
                idLabelMap={
                  enabledVariant === TABLE_VARIANT.Combined
                    ? idLabelMapForCombinedTable
                    : idLabelMapForFlatTable
                }
              />
            </SimInventoryCan>
          ),
          renderCell: (props) => <ActionCell actions={mappedAction} data={props.row} />,
        });
      }

      return columns;
    }, [configuration, enabledVariant, sort, isSessionDetailsLoading, fontScale]);

    const initialState = useMemo(() => {
      const visibilityModel = configuration.reduce(
        (visibilityModel: Record<string, boolean>, columnConfiguration: ColumnConfiguration) => {
          return {
            ...visibilityModel,
            [columnConfiguration.name]: columnConfiguration.enabled,
          };
        },
        {} as Record<string, boolean>,
      );

      return {
        columns: {
          columnVisibilityModel: { ...visibilityModel, simStatus: false },
        },
      };
    }, [configuration]);

    const handleVariantChange = useCallback(
      async (isChecked: boolean) => {
        updateVariant(isChecked ? TABLE_VARIANT.Combined : TABLE_VARIANT.Flat);
      },
      [updateVariant],
    );

    const onColumnWidthChange = useCallback(
      (params: any) => {
        const matchedColumn = configuration.find((row) => row.name === params.colDef.field);
        if (matchedColumn) {
          updateWidthOfColumn(params.colDef.field, params.width, matchedColumn.isCustomField);
        }
      },
      [updateWidthOfColumn, configuration],
    );

    const onColumnOrderChange = useCallback(
      (params: GridColumnOrderChangeParams) => {
        const draggedColumnIndex: number = params.oldIndex;
        const targetIndex: number = params.targetIndex;

        if (selectedIds) {
          reorder(draggedColumnIndex - 1, targetIndex - 1);
        } else {
          reorder(draggedColumnIndex, targetIndex);
        }
      },
      [reorder],
    );

    const onColumnVisibilityModelChange = useCallback(
      (model: GridColumnVisibilityModel) => {
        updateVisibilityOfColumns(model);
      },
      [updateVisibilityOfColumns],
    );

    const paginationModel = useMemo(() => {
      return {
        page: page,
        pageSize: pageSize,
      };
    }, [page, pageSize]);

    const sortModel = useMemo(
      () =>
        sort
          ? [
              {
                field: sort.columnName,
                sort: sort.sort,
              },
            ]
          : [],
      [sort],
    );

    const rowSelectionModel = useMemo(() => {
      if (selectedIds) {
        return {
          ids: selectedIds,
          type: 'include',
        } as GridRowSelectionModel;
      }
    }, [selectedIds]);

    const theme = useTheme();

    return (
      <Box sx={{ width: '100%', height: '100%' }}>
        <DndProvider backend={HTML5Backend}>
          <DataGridPro<SimData>
            {...props}
            disableRowSelectionExcludeModel
            checkboxSelection={!!selectedIds}
            sortModel={sortModel}
            disableColumnSorting
            sortingMode="server"
            sortingOrder={['desc', 'asc']}
            getRowClassName={getRowClassName}
            getRowId={getRowId}
            sx={{
              backgroundColor: 'white',
              border: 'none',
              width: '100%',
              height: '100%',
              '& .MuiDataGrid-row': {
                height:
                  enabledVariant === TABLE_VARIANT.Combined ? theme.spacing(52) : theme.spacing(42),
              },
            }}
            slotProps={{
              root: {
                'data-testid': testid,
              },
            }}
            disableRowSelectionOnClick
            columnBufferPx={2000000}
            rowBufferPx={520}
            onColumnWidthChange={onColumnWidthChange}
            onColumnOrderChange={onColumnOrderChange}
            onColumnVisibilityModelChange={onColumnVisibilityModelChange}
            getRowHeight={() => 'auto'}
            columns={columns}
            disableAutosize
            rows={rows}
            initialState={initialState}
            pagination
            paginationMode="server"
            onPaginationModelChange={onPaginationChange}
            paginationModel={paginationModel}
            rowSelectionModel={rowSelectionModel}
            pinnedColumns={{
              right: ['actions'],
            }}
            slots={{
              noRowsOverlay: DefaultNoRowsOverlay,
              pagination: Pagination,
              columnResizeIcon: DefaultGridSeparatorIcon,
            }}
          />
        </DndProvider>
        {dialogActions.map((action: any) => {
          return (
            <React.Fragment key={action.id}>
              <action.Component
                open={!!dialogs[action.id]}
                data={dialogs[action.id]}
                onClose={() => {
                  unselectDialog(action.id);
                }}
                slotProps={action.slotProps}
              />
            </React.Fragment>
          );
        })}
      </Box>
    );
  },
);
